/**
 * GA4 DataLayer - AJAX Event Handlers
 * REQ-010: GA4 & GTM Ecommerce Tracking
 *
 * Handles events that occur without page reload:
 * - add_to_cart: Listens for theme's 'add-to-cart' custom event
 * - view_cart: Fires when cart drawer opens (MutationObserver on sidebar)
 *
 * Dependencies:
 * - dataLayer must be initialized (by ga4-datalayer-init.liquid)
 * - Theme must dispatch 'add-to-cart' event (component-product-form.js:823)
 * - Cart sidebar must have id="site-cart-sidebar" (theme.liquid)
 */

/* global MutationObserver */

(function () {
  'use strict';

  // Ensure dataLayer exists
  window.dataLayer = window.dataLayer || [];

  /**
   * Helper: Map cart.js response to GA4 items array
   * @param {Array} items - Cart items from /cart.js
   * @returns {Array} GA4-formatted items
   */
  function mapCartItems(items) {
    return items.map(function (item) {
      return {
        item_id: String(item.variant_id),
        item_name: item.product_title,
        item_brand: item.vendor || '',
        item_category: item.product_type || '',
        item_variant: item.variant_title || 'Default Title',
        price: item.final_price / 100,
        quantity: item.quantity
      };
    });
  }

  /**
   * Helper: Fetch current cart data from Shopify
   * @returns {Promise<Object>} Cart data
   */
  function fetchCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }).then(function (response) {
      return response.json();
    });
  }

  /**
   * add_to_cart Event Handler
   * REQ-010: FR-002
   *
   * The theme dispatches 'add-to-cart' on the product-form element itself,
   * and the event does NOT bubble (created with new Event() without {bubbles: true}).
   * We must attach listeners directly to each product-form element.
   */
  function initAddToCartTracking() {
    // Attach listener to all existing product-form elements
    document.querySelectorAll('product-form').forEach(attachAddToCartListener);

    // Watch for dynamically added product-form elements (quick view modals, etc.)
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) {
            if (node.tagName === 'PRODUCT-FORM') {
              attachAddToCartListener(node);
            }
            if (node.querySelectorAll) {
              node.querySelectorAll('product-form').forEach(attachAddToCartListener);
            }
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Attach add-to-cart listener to a product-form element
   * @param {Element} productForm - The product-form custom element
   */
  function attachAddToCartListener(productForm) {
    if (productForm.hasAttribute('data-ga4-listener')) return;
    productForm.setAttribute('data-ga4-listener', 'true');

    productForm.addEventListener('add-to-cart', function () {
      var form = productForm.querySelector('form');
      if (!form) return;

      var variantInput = form.querySelector('input[name="id"]');
      var quantityInput = form.querySelector('[data-js-product-quantity]');

      if (!variantInput) return;

      var variantId = variantInput.value;
      var quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

      fetchCart().then(function (cart) {
        var addedItem = cart.items.find(function (item) {
          return String(item.variant_id) === String(variantId);
        });

        if (!addedItem) return;

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            currency: cart.currency,
            value: (addedItem.final_price * quantity) / 100,
            items: [
              {
                item_id: String(addedItem.variant_id),
                item_name: addedItem.product_title,
                item_brand: addedItem.vendor || '',
                item_category: addedItem.product_type || '',
                item_variant: addedItem.variant_title || 'Default Title',
                price: addedItem.final_price / 100,
                quantity: quantity
              }
            ]
          }
        });
      });
    });
  }

  /**
   * view_cart Event Handler
   * REQ-010: FR-003
   *
   * Fires when the cart drawer opens
   * Uses MutationObserver to detect when sidebar becomes visible
   */
  function initViewCartTracking() {
    var cartSidebar = document.getElementById('site-cart-sidebar');
    if (!cartSidebar) {
      // Cart sidebar not present (might be on cart page template)
      return;
    }

    // Track if we've already fired for this open
    var hasFiresForThisOpen = false;

    // Create observer to watch for sidebar opening
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          var isOpen = cartSidebar.classList.contains('sidebar--opened');

          if (isOpen && !hasFiresForThisOpen) {
            hasFiresForThisOpen = true;
            fireViewCartEvent();
          } else if (!isOpen) {
            // Reset flag when sidebar closes
            hasFiresForThisOpen = false;
          }
        }
      });
    });

    // Start observing
    observer.observe(cartSidebar, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /**
   * Fire the view_cart event
   * Fetches fresh cart data to ensure accuracy
   */
  function fireViewCartEvent() {
    fetchCart().then(function (cart) {
      if (cart.item_count === 0) {
        // Don't fire view_cart for empty carts
        return;
      }

      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'view_cart',
        ecommerce: {
          currency: cart.currency,
          value: cart.total_price / 100,
          items: mapCartItems(cart.items)
        }
      });
    });
  }

  /**
   * Initialize all tracking
   */
  function init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        initAddToCartTracking();
        initViewCartTracking();
      });
    } else {
      initAddToCartTracking();
      initViewCartTracking();
    }
  }

  // Start
  init();
})();
